import { Button, ListView, PageHeader, useCursorList } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, ok } from "../../api";
import { PRODUCT_TYPE, type ProductType, productActions, productError } from "../../products";
import { meQuery } from "../../session";
import { CategoriesDialog, CategorySelect } from "./categories";
import { blankItem, ItemFields, itemReady, toNewItem } from "./items";
import styles from "./products.module.css";

/** Products (P8-18, `Console / Products` in the design): what the company sells. */
export function ProductsPage(): ReactNode {
  const { data: me } = useSuspenseQuery(meQuery);
  const canManage = me?.permissions.includes("catalog.manage") ?? false;
  const [adding, setAdding] = useState(false);
  const [categories, setCategories] = useState(false);
  const list = useCursorList({
    queryKey: ["catalog", "products", "list"],
    fetchPage: (cursor) => ok(api.GET("/v1/admin/catalog/products", { params: { query: { cursor, limit: 25 } } })),
  });
  return (
    <>
      <PageHeader
        title="Products"
        description="What you sell, and the items customers order."
        actions={
          canManage ? (
            <span className={styles.actions}>
              <Button variant="secondary" onClick={() => setCategories(true)}>
                Categories
              </Button>
              <Button onClick={() => setAdding(true)}>Add product</Button>
            </span>
          ) : undefined
        }
      />
      <ListView
        caption="Products"
        itemName={{ one: "product", other: "products" }}
        rows={list.rows}
        rowKey={(p) => p.id}
        status={list.status}
        errorMessage={list.error ? productError(list.error) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={{ title: "No products yet", description: "Add what you sell: water loads, tanks, services." }}
        columns={[
          {
            key: "name",
            label: "Product",
            render: (p) => (
              <Link to="/products/$productId" params={{ productId: p.id }} className={styles.link}>
                {p.name}
              </Link>
            ),
          },
          { key: "type", label: "Type", render: (p) => <span className={styles.muted}>{PRODUCT_TYPE[p.type]}</span> },
          {
            key: "category",
            label: "Category",
            render: (p) => <span className={styles.muted}>{p.category?.name ?? "—"}</span>,
          },
          {
            key: "items",
            label: "Items",
            render: (p) => p.items.map((i) => (i.is_active ? i.name : `${i.name} (inactive)`)).join(", ") || "No items",
          },
          {
            key: "is_active",
            label: "Status",
            render: (p) => (
              <Badge tone={p.is_active ? "success" : "neutral"}>{p.is_active ? "On sale" : "Inactive"}</Badge>
            ),
          },
        ]}
      />
      {adding ? <AddProduct onClose={() => setAdding(false)} /> : null}
      {categories ? <CategoriesDialog onClose={() => setCategories(false)} /> : null}
    </>
  );
}

/** A product needs at least one item: its first one is added with it. */
function AddProduct({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState<ProductType>("water");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [item, setItem] = useState(() => blankItem(true));
  const create = useMutation({
    mutationFn: () =>
      productActions.create({
        name: name.trim(),
        type,
        description: description.trim() || undefined,
        category_id: categoryId ?? undefined,
        items: [toNewItem(item)],
      }),
    onSuccess: async (product) => {
      await queryClient.invalidateQueries({ queryKey: ["catalog"] });
      onClose();
      void navigate({ to: "/products/$productId", params: { productId: product.id } });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="Add a product" description="With its first item. Add more items on its page.">
        <div className={styles.form}>
          <div className={styles.pair}>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Water delivery" />
            <Select
              label="Type"
              value={type}
              onValueChange={(v) => {
                setType(v as ProductType);
                setItem(blankItem(v === "water"));
              }}
              options={(Object.keys(PRODUCT_TYPE) as ProductType[]).map((t) => ({ value: t, label: PRODUCT_TYPE[t] }))}
            />
          </div>
          <Textarea
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          <CategorySelect value={categoryId} onChange={setCategoryId} />
          <ItemFields item={item} onChange={setItem} />
          <p className={styles.muted}>Set its prices under Pricing.</p>
          {create.error ? (
            <Alert tone="danger" title="Not added">
              {productError(create.error)}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!name.trim() || !itemReady(item)}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Add product
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
